import React, { useMemo, useState } from "react";
import { Box, Text, UnstyledButton } from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";
import {
  ALL_APPOINTMENT_STATUSES,
  AppointmentStatus,
} from "../../../../../types/calendar";
import {
  expandAll,
  isSelected,
  toggleInSelection,
} from "../../components/Views/filterSelection";
import {
  passesUserFilters,
  UserFilterSelection,
} from "../../hooks/eventFilters";
import { MobileAppt } from "../grid/geometry";
import { STATUS_TONES } from "../tokens";
import { MobileSheet } from "./MobileSheet";
import styles from "./MobileFiltersSheet.module.css";

export interface MobileFilterSelection extends UserFilterSelection {
  /** Only providers with working hours on the viewed date (CalendarContext
   *  `workingToday`, shared with desktop's "Working today"). */
  workingOnly: boolean;
}

export interface MobileFiltersSheetProps {
  opened: boolean;
  onClose: () => void;
  staff: { id: string; name: string }[];
  serviceTypes: { name: string; color: string }[];
  /** Applied selection; the sheet edits a pending copy of it. */
  selection: MobileFilterSelection;
  /** The viewed day's appointments (all staff, all statuses). */
  dayAppts: MobileAppt[];
  /** Providers with working hours on the viewed date. */
  workingStaffIds: Set<string>;
  /** Commit the pending selection ("Show N"). */
  onApply: (selection: MobileFilterSelection) => void;
}

const CheckBox: React.FC<{ on: boolean }> = ({ on }) => (
  <Box component="span" className={styles.box} data-on={on}>
    <IconCheck size={12} stroke={3} />
  </Box>
);

/** Appointments of the day the pending selection would show. */
export function countShown(
  dayAppts: MobileAppt[],
  selection: UserFilterSelection,
): number {
  return dayAppts.filter(
    (a) => !a.isBlock && passesUserFilters(a.event, selection),
  ).length;
}

const FiltersBody: React.FC<Omit<MobileFiltersSheetProps, "opened">> = ({
  onClose,
  staff,
  serviceTypes,
  selection,
  dayAppts,
  workingStaffIds,
  onApply,
}) => {
  const [pending, setPending] = useState<MobileFilterSelection>(selection);
  const staffIds = useMemo(() => staff.map((s) => s.id), [staff]);
  const typeNames = useMemo(
    () => serviceTypes.map((t) => t.name),
    [serviceTypes],
  );

  const countByStaff = useMemo(() => {
    const counts: Record<string, number> = {};
    dayAppts.forEach((a) => {
      if (a.isBlock) return;
      counts[a.staffId] = (counts[a.staffId] || 0) + 1;
    });
    return counts;
  }, [dayAppts]);

  const shown = countShown(dayAppts, pending);
  const rows = pending.workingOnly
    ? staff.filter((s) => workingStaffIds.has(s.id))
    : staff;

  const allStaffOn = staffIds.every((id) =>
    isSelected(pending.selectedArtists, id),
  );
  const allTypesOn = typeNames.every((t) =>
    isSelected(pending.selectedServiceTypes, t),
  );
  const allStatusesOn =
    pending.selectedStatuses.length === ALL_APPOINTMENT_STATUSES.length;

  const update = (patch: Partial<MobileFilterSelection>) =>
    setPending((p) => ({ ...p, ...patch }));

  const toggleStatus = (status: AppointmentStatus) =>
    setPending((p) => ({
      ...p,
      selectedStatuses: p.selectedStatuses.includes(status)
        ? p.selectedStatuses.filter((s) => s !== status)
        : [...p.selectedStatuses, status],
    }));

  return (
    <>
      <Box className={styles.sectionHead}>
        <Text span className={styles.micro}>
          Staff
        </Text>
        <UnstyledButton
          className={styles.link}
          onClick={() =>
            update({
              selectedArtists: allStaffOn ? [] : ["all", ...staffIds],
            })
          }
        >
          {allStaffOn ? "Clear all" : "Select all"}
        </UnstyledButton>
      </Box>
      <UnstyledButton
        className={styles.working}
        data-on={pending.workingOnly}
        role="checkbox"
        aria-checked={pending.workingOnly}
        title="Only staff with working hours on this day"
        onClick={() => update({ workingOnly: !pending.workingOnly })}
      >
        <CheckBox on={pending.workingOnly} />
        <Text span inherit className={styles.rowLabel}>
          Working only
        </Text>
      </UnstyledButton>
      <Box className={styles.rows}>
        {rows.map((s) => {
          const on = isSelected(pending.selectedArtists, s.id);
          const count = countByStaff[s.id] || 0;
          return (
            <UnstyledButton
              key={s.id}
              className={styles.row}
              data-on={on}
              role="checkbox"
              aria-checked={on}
              onClick={() =>
                setPending((p) => ({
                  ...p,
                  selectedArtists: toggleInSelection(
                    expandAll(p.selectedArtists, staffIds),
                    s.id,
                    staffIds,
                  ),
                }))
              }
            >
              <CheckBox on={on} />
              <Text span inherit className={styles.rowLabel}>
                {s.name}
              </Text>
              <Text span className={styles.count}>
                {count || "—"}
              </Text>
            </UnstyledButton>
          );
        })}
      </Box>

      <Box className={styles.sectionHead} data-spaced="true">
        <Text span className={styles.micro}>
          Services
        </Text>
        <UnstyledButton
          className={styles.link}
          onClick={() =>
            update({
              selectedServiceTypes: allTypesOn ? [] : ["all", ...typeNames],
            })
          }
        >
          {allTypesOn ? "Clear all" : "Select all"}
        </UnstyledButton>
      </Box>
      <Box className={styles.rows} data-services="true">
        {serviceTypes.map((t) => {
          const on = isSelected(pending.selectedServiceTypes, t.name);
          return (
            <UnstyledButton
              key={t.name}
              className={styles.row}
              data-service="true"
              data-on={on}
              role="checkbox"
              aria-checked={on}
              onClick={() =>
                setPending((p) => ({
                  ...p,
                  selectedServiceTypes: toggleInSelection(
                    expandAll(p.selectedServiceTypes, typeNames),
                    t.name,
                    typeNames,
                  ),
                }))
              }
            >
              <CheckBox on={on} />
              <Box
                component="span"
                className={styles.swatch}
                data-on={on}
                style={{ background: t.color }}
              />
              <Text span inherit className={styles.rowLabel}>
                {t.name}
              </Text>
            </UnstyledButton>
          );
        })}
      </Box>

      <Box className={styles.sectionHead} data-spaced="true">
        <Text span className={styles.micro}>
          Status
        </Text>
        <UnstyledButton
          className={styles.link}
          onClick={() =>
            update({
              selectedStatuses: allStatusesOn
                ? []
                : [...ALL_APPOINTMENT_STATUSES],
            })
          }
        >
          {allStatusesOn ? "Clear all" : "Select all"}
        </UnstyledButton>
      </Box>
      <Box className={styles.pills}>
        {ALL_APPOINTMENT_STATUSES.map((status) => {
          const on = pending.selectedStatuses.includes(status);
          // Fall back so a status without a tone can't crash the sheet.
          const tone = STATUS_TONES[status] ?? {
            ...STATUS_TONES.Requested,
            label: status,
          };
          return (
            <UnstyledButton
              key={status}
              className={styles.pill}
              data-on={on}
              aria-pressed={on}
              onClick={() => toggleStatus(status)}
              style={
                on
                  ? ({
                      "--tone-bg": tone.bg,
                      "--tone-fg": tone.fg,
                      "--tone-bd": tone.bd,
                    } as React.CSSProperties)
                  : undefined
              }
            >
              {tone.label}
            </UnstyledButton>
          );
        })}
      </Box>

      <Box className={styles.footer}>
        <UnstyledButton
          className={styles.show}
          onClick={() => {
            onApply(pending);
            onClose();
          }}
        >
          Show {shown} {shown === 1 ? "appointment" : "appointments"}
        </UnstyledButton>
      </Box>
    </>
  );
};

/**
 * Filters sheet: staff (all/none, Working only, per-day counts), services
 * (swatches), and status pills in their status colors. Edits a pending copy;
 * the sticky "Show N" counts that pending selection live and applies it.
 * Closing without it discards the pending changes.
 */
export const MobileFiltersSheet: React.FC<MobileFiltersSheetProps> = ({
  opened,
  ...rest
}) => (
  <MobileSheet
    opened={opened}
    onClose={rest.onClose}
    title="Filters"
    subtitle="Staff, services and statuses shown on this day"
    data-testid="mobile-filters-sheet"
  >
    <FiltersBody {...rest} />
  </MobileSheet>
);
